// apps/account-portal/src/components/SubscriptionInfoCard.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Subscription } from '../stores/subscriptionStore';

interface SubscriptionInfoCardProps {
  subscription: Subscription | null | undefined;
  subscriptionsLoaded: boolean;
  isCanceling: boolean;
  onCancelClick: () => void;
}

const SubscriptionInfoCard: React.FC<SubscriptionInfoCardProps> = ({
  subscription,
  subscriptionsLoaded,
  isCanceling,
  onCancelClick,
}) => {
  const { t } = useTranslation();

  // 資料尚未載入完成，不顯示任何內容
  if (!subscriptionsLoaded) return null;

  // 無訂閱記錄：預設免費方案
  if (!subscription) {
    return (
      <div className="bg-brand-50/60 dark:bg-brand-950/30 border border-brand-100 dark:border-brand-900/50 rounded-lg p-4 mb-5">
        <div className="flex items-center space-x-3 ml-4">
          <span className="text-base font-semibold text-[var(--text-primary)]">
            {t('pages.subscription.plan_free')}
          </span>
          <span className="inline-flex px-2 py-0.5 text-[13px] font-semibold rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400">
            {t('subscription.status.active')}
          </span>
          <span className="text-sm text-[var(--text-secondary)]">
            {t('subscription.permanent')}
          </span>
        </div>
      </div>
    );
  }

  const isFree = subscription.plan.id?.includes('_free') || subscription.plan.price === 0;

  return (
    <div className="bg-brand-50/60 dark:bg-brand-950/30 border border-brand-100 dark:border-brand-900/50 rounded-lg p-4 mb-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 sm:ml-4">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-base font-semibold text-[var(--text-primary)]">
              {subscription.plan.name}
            </span>
            <span className={`inline-flex px-2 py-0.5 text-[13px] font-semibold rounded-full ${
              subscription.status === 'CANCELLED'
                ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                : subscription.status === 'EXPIRED'
                ? 'bg-[var(--page-bg)] text-[var(--text-secondary)]'
                : 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400'
            }`}>
              {t(`subscription.status.${subscription.status.toLowerCase()}`)}
            </span>
            {isFree ? (
              <span className="text-sm text-[var(--text-secondary)]">
                {t('subscription.permanent')}
              </span>
            ) : (
              <span className="text-sm text-[var(--text-secondary)]">
                {subscription.status === 'TRIAL' ? t('subscription.trial_ends') : t('subscription.next_billing')}
                {': '}
                {subscription.trialEndDate
                  ? new Date(subscription.trialEndDate).toLocaleDateString()
                  : new Date(subscription.endDate).toLocaleDateString()
                }
              </span>
            )}
          </div>

          {!isFree && (
            <div className="text-base font-bold tabular-nums text-[var(--text-primary)]">
              {t('pricing.currency', { ns: 'staticContent' })}{subscription.plan.price.toLocaleString()}
              <span className="text-sm font-normal text-[var(--text-secondary)] ml-1">
                / {t('subscription.per_month')}
              </span>
            </div>
          )}
        </div>

        {!isFree && subscription.status !== 'CANCELLED' && (
          <button
            onClick={onCancelClick}
            disabled={isCanceling}
            className="self-start sm:self-auto flex-shrink-0 px-4 py-2 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isCanceling ? t('common.status.processing') : t('subscription.cancel_subscription')}
          </button>
        )}
      </div>
    </div>
  );
};

export default SubscriptionInfoCard;
